import React from 'react'
import { Dropdown, MenuProps, Tag } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import { MenuInfo } from 'rc-menu/lib/interface'
import classNames from 'classnames'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import styles from './StatusDropdown.module.scss'

interface IStatusDropdownProps {
  items: MenuProps['items']
  color: string
  label: string
  className?: string
  onClick: (e: MenuInfo) => void
}

interface IStatusOption {
  key: string
  text: string
}

// подписи пунктов приходят разметкой (<Tag>Статус</Tag>), а <option> принимает только строку
const getItemText = (node: React.ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node)
  }
  if (Array.isArray(node)) {
    return node.map(getItemText).join('')
  }
  if (React.isValidElement(node)) {
    return getItemText((node.props as { children?: React.ReactNode }).children)
  }
  return ''
}

export const StatusDropdown: React.FC<IStatusDropdownProps> = ({ items, color, label, className, onClick }) => {
  const isMobile = useIsMobile()

  const options = React.useMemo<IStatusOption[]>(
    () =>
      (items ?? []).reduce<IStatusOption[]>((acc, item) => {
        if (!item || !('label' in item) || item.key == null) {
          return acc
        }
        return [...acc, { key: String(item.key), text: getItemText(item.label) }]
      }, []),
    [items]
  )

  // Менять статус может не каждая роль: без доступных пунктов показываем статус без стрелки
  if (!options.length) {
    return (
      <Tag color={color} className={className}>
        {label}
      </Tag>
    )
  }

  const tag = (
    <Tag
      color={color}
      icon={<DownOutlined className={styles.icon} />}
      className={styles.tag}
    >
      {label}
    </Tag>
  )

  // На телефоне статус выбирают штатным меню системы: поверх тега лежит прозрачный
  // <select>, тап по нему открывает выбор iOS или Android вместо списка antd
  if (isMobile) {
    return (
      <span className={classNames(styles.nativeWrapper, className)}>
        {tag}
        <select
          className={styles.nativeSelect}
          value=''
          aria-label='Изменить статус'
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => {
            const { value } = event.target
            if (!value) {
              return
            }
            onClick({ key: value } as MenuInfo)
          }}
        >
          {/* первым пунктом текущий статус: он же остаётся выбранным, если меню закрыли */}
          <option value=''>{label}</option>
          {options.map((option) => (
            <option key={option.key} value={option.key}>
              {option.text}
            </option>
          ))}
        </select>
      </span>
    )
  }

  return (
    <Dropdown
      menu={{ items, onClick }}
      trigger={['click']}
      className={className}
    >
      {tag}
    </Dropdown>
  )
}
